import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, History, ListChecks, Phone, RecycleIcon, RefreshCcw, RotateCcw, Search, ShieldAlert, Trash2, User,
} from "lucide-react";
import { EmptyState, PageHeader, Spinner } from "../../components/common";
import { ACTION_LABELS, fmtMobile } from "../../components/MobileVerifyGate";
import { MobileChangeLogReport } from "../../components/MobileChangeLogReport";   // Phase 204
import { PaginationBar } from "../../components/PaginationBar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, errMsg, fmtDateTime } from "../../lib/api";

/**
 * Phase 134 — Super Admin › Mobile Numbers.
 * Browse a property's owner-mobile history (by PIN) in any municipality. Only the Super Admin may delete a
 * number: Recycle (soft delete, restorable) or Delete permanently — and never the CURRENT number.
 */
const Recycle = RecycleIcon;

function ActorCell({ row }) {
  return (
    <div className="text-xs">
      <p className="flex items-center gap-1"><User className="h-3 w-3 text-muted-foreground" /> {row.by_label}</p>
      <p className="text-[11px] text-muted-foreground">{row.by_role_label || ""}</p>
    </div>
  );
}

export default function MobileNumbers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [munis, setMunis] = useState(null);
  const [code, setCode] = useState(searchParams.get("muni") || "");
  const [tab, setTab] = useState("history");
  const [pin, setPin] = useState(searchParams.get("pin") || "");
  const [data, setData] = useState(null);       // property history
  const [loading, setLoading] = useState(false);
  const [bin, setBin] = useState(null);         // recycle bin page
  const [binPage, setBinPage] = useState(1);
  const [binLoading, setBinLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // {kind: recycle|restore|purge, row}
  const [typed, setTyped] = useState("");

  const muni = useMemo(() => (munis || []).find((m) => m.code === code) || null, [munis, code]);

  useEffect(() => {
    api.get("/platform/municipalities")
      .then((r) => setMunis((r.data || []).filter((m) => m.is_active !== false)))
      .catch((e) => { toast.error(errMsg(e)); setMunis([]); });
  }, []);

  const selectMuni = (c) => {
    setCode(c); setData(null); setBin(null); setBinPage(1);
    setSearchParams(c ? { muni: c } : {});
  };

  const lookup = useCallback(async (e) => {
    e?.preventDefault?.();
    const v = pin.trim();
    if (!code || !v) return;
    setLoading(true);
    try {
      const r = await api.get(`/platform/municipalities/${code}/mobile-history`, { params: { pin: v } });
      setData(r.data);
      setSearchParams({ muni: code, pin: v });
    } catch (err) { toast.error(errMsg(err)); setData(null); } finally { setLoading(false); }
  }, [code, pin, setSearchParams]);

  const loadBin = useCallback(() => {
    if (!code) return;
    setBinLoading(true);
    api.get(`/platform/municipalities/${code}/mobile-history/recycled`, { params: { page: binPage, limit: 25 } })
      .then((r) => setBin(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setBinLoading(false));
  }, [code, binPage]);
  useEffect(() => { if (tab === "bin") loadBin(); }, [tab, loadBin]);

  // arriving with ?muni=&pin= runs the lookup once
  useEffect(() => { if (code && searchParams.get("pin") && !data && !loading) lookup(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAll = () => { if (data) lookup(); if (tab === "bin") loadBin(); };

  const act = async () => {
    if (!confirm) return;
    const { kind, row } = confirm;
    setBusy(true);
    try {
      if (kind === "recycle") await api.post(`/platform/municipalities/${code}/mobile-history/${row.id}/recycle`);
      else if (kind === "restore") await api.post(`/platform/municipalities/${code}/mobile-history/${row.id}/restore`);
      else await api.delete(`/platform/municipalities/${code}/mobile-history/${row.id}`);
      toast.success(kind === "recycle" ? `${fmtMobile(row.mobile)} moved to the recycle bin`
        : kind === "restore" ? `${fmtMobile(row.mobile)} restored to the history`
          : `${fmtMobile(row.mobile)} deleted permanently`);
      setConfirm(null); setTyped("");
      if (data) await lookup();
      if (tab === "bin" || kind !== "recycle") loadBin();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const numbers = data?.numbers || [];
  const old = numbers.filter((n) => !n.is_current);
  const current = numbers.find((n) => n.is_current);
  const purgeOk = confirm?.kind !== "purge" || typed.trim().toUpperCase() === "DELETE";

  return (
    <div className="space-y-5 pb-16" data-testid="super-mobile-numbers-page">
      <PageHeader title="Mobile Numbers" subtitle="Owner mobile number history per property — who changed it and when. Only you can recycle or permanently delete an old number; the current number is always protected.">
        <Button variant="outline" size="sm" onClick={refreshAll} disabled={!code || loading || binLoading} data-testid="mobile-numbers-refresh-button">
          <RefreshCcw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </PageHeader>

      <Card className="shadow-sm">
        <CardContent className="p-4 sm:p-5 flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="space-y-1.5 flex-1 max-w-md">
            <Label>Municipality</Label>
            {!munis ? <Spinner label="Loading municipalities..." /> : (
              <Select value={code} onValueChange={selectMuni}>
                <SelectTrigger data-testid="mobile-numbers-muni-select"><SelectValue placeholder="Select a municipality" /></SelectTrigger>
                <SelectContent>
                  {munis.map((m) => (
                    <SelectItem key={m.code} value={m.code} data-testid={`mobile-numbers-muni-option-${m.code}`}>
                      {m.name} <span className="text-muted-foreground font-mono text-xs">({m.code})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <form onSubmit={lookup} className="space-y-1.5 flex-1 max-w-md">
            <Label htmlFor="mobile-numbers-pin">Property PIN</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="mobile-numbers-pin" className="pl-9 font-mono" placeholder="e.g. BMC100001" value={pin} disabled={!code}
                  onChange={(e) => setPin(e.target.value)} data-testid="mobile-numbers-pin-input" />
              </div>
              <Button type="submit" disabled={!code || !pin.trim() || loading} data-testid="mobile-numbers-search-button">
                {loading ? "Searching…" : "Search"}
              </Button>
            </div>
          </form>
          <div className="text-xs bg-destructive/10 text-destructive rounded-md px-3 py-2 flex gap-2 flex-1" data-testid="mobile-numbers-warning">
            <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
            <span><span className="font-semibold">Recycle</span> hides an old number from every history view (restorable). <span className="font-semibold">Delete permanently</span> is irreversible. The current number can never be deleted.</span>
          </div>
        </CardContent>
      </Card>

      {!code ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <EmptyState title="Pick a municipality to begin" subtitle="Then search a property PIN to see its mobile number history, or open the Recycle Bin." />
        </CardContent></Card>
      ) : (
        <>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="mobile-numbers-tabs">
              <TabsTrigger value="history" className="gap-1.5" data-testid="mobile-numbers-tab-history"><History className="h-3.5 w-3.5" /> Number history</TabsTrigger>
              <TabsTrigger value="changelog" className="gap-1.5" data-testid="mobile-numbers-tab-changelog"><ListChecks className="h-3.5 w-3.5" /> Change Log</TabsTrigger>
              <TabsTrigger value="bin" className="gap-1.5" data-testid="mobile-numbers-tab-bin"><Recycle className="h-3.5 w-3.5" /> Recycle Bin{bin ? ` (${bin.total})` : ""}</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Phase 204: the same Mobile Number Change Log the Municipal Admin sees under Reports (municipality-wide) */}
          {tab === "changelog" && (
            <div data-testid="mobile-numbers-changelog">
              <p className="text-xs text-muted-foreground mb-3" data-testid="mobile-numbers-changelog-note">
                Every "Update number" and "Number is correct" event recorded by Field Collectors and Supervisors in {muni?.name || "this municipality"}, with the receipt collected in the same visit. Municipal Admins see this report under Reports only after you grant <span className="font-medium text-foreground">MIS Reports → Mobile Number Change Log</span> in Access Control.
              </p>
              <MobileChangeLogReport code={code} testId="sa-mcl" />
            </div>
          )}

          {tab === "history" && (
            !data ? (
              <Card className="shadow-sm"><CardContent className="p-0">
                <EmptyState title="Search a property PIN" subtitle={`Enter a PIN of ${muni?.name || "this municipality"} above to load its mobile number history.`} />
              </CardContent></Card>
            ) : (
              <div className="space-y-4" data-testid="mobile-numbers-result">
                <Card className="shadow-sm" data-testid="mobile-numbers-property-card">
                  <CardContent className="p-4 flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="font-mono font-semibold text-base" data-testid="mobile-numbers-property-pin">{data.property.pin}</p>
                      <p className="font-medium">{data.property.owner_name || "—"}</p>
                      <p className="text-xs text-muted-foreground">{data.property.address || ""}</p>
                    </div>
                    <div className="rounded-lg border bg-muted/40 px-3 py-2 flex items-center gap-3">
                      <Phone className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-xs text-muted-foreground">Current number</p>
                        <p className="text-lg font-semibold tabular-nums tracking-wide" data-testid="mobile-numbers-current">{data.current_mobile ? fmtMobile(data.current_mobile) : "—"}</p>
                        {current?.action === "updated" && <p className="text-[11px] text-muted-foreground">set by {current.by_label} · {fmtDateTime(current.at)}</p>}
                      </div>
                      <Badge variant="outline" className="gap-1 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20"><CheckCircle2 className="h-3 w-3" /> Protected</Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-sm">
                  <CardContent className="p-0">
                    <div className="px-4 py-3 border-b flex items-center justify-between">
                      <p className="font-medium text-sm flex items-center gap-1.5"><History className="h-4 w-4" /> Old numbers <Badge variant="secondary" data-testid="mobile-numbers-old-count">{old.length}</Badge></p>
                      <p className="text-xs text-muted-foreground">{data.counts?.confirmations || 0} confirmation{data.counts?.confirmations === 1 ? "" : "s"} · {data.recycled?.length || 0} in recycle bin</p>
                    </div>
                    {old.length === 0 ? (
                      <p className="text-sm text-muted-foreground px-4 py-8 text-center" data-testid="mobile-numbers-old-empty">No old numbers — this property's number has never been changed (or all old numbers were removed).</p>
                    ) : (
                      <Table data-testid="mobile-numbers-old-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Old number</TableHead>
                            <TableHead>Entered by</TableHead>
                            <TableHead>Entered on</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {old.map((n) => (
                            <TableRow key={n.id} data-testid={`mobile-numbers-old-row-${n.mobile}`}>
                              <TableCell className="font-medium tabular-nums">{fmtMobile(n.mobile)}<p className="text-[11px] text-muted-foreground">{ACTION_LABELS[n.action] || n.action}{n.previous_mobile ? ` · replaced ${fmtMobile(n.previous_mobile)}` : ""}</p></TableCell>
                              <TableCell><ActorCell row={n} /></TableCell>
                              <TableCell className="text-xs tabular-nums">{fmtDateTime(n.at)}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button size="sm" variant="outline" className="h-8" disabled={!n.can_delete} onClick={() => setConfirm({ kind: "recycle", row: n })} data-testid={`mobile-numbers-recycle-${n.mobile}`}>
                                    <Recycle className="h-3.5 w-3.5 mr-1" /> Recycle
                                  </Button>
                                  <Button size="sm" variant="outline" className="h-8 text-destructive border-destructive/40 hover:bg-destructive/10" disabled={!n.can_delete} onClick={() => setConfirm({ kind: "purge", row: n })} data-testid={`mobile-numbers-purge-${n.mobile}`}>
                                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete permanently
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>

                {data.recycled?.length > 0 && (
                  <Card className="shadow-sm border-dashed" data-testid="mobile-numbers-property-recycled">
                    <CardContent className="p-4">
                      <p className="text-sm font-medium flex items-center gap-1.5 mb-2"><Recycle className="h-4 w-4" /> In the recycle bin for this property</p>
                      <ul className="space-y-1">
                        {data.recycled.map((r) => (
                          <li key={r.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`mobile-numbers-recycled-row-${r.mobile}`}>
                            <span><span className="tabular-nums font-medium">{fmtMobile(r.mobile)}</span> · recycled {fmtDateTime(r.recycled_at)} by {r.recycled_by_name || "Super Admin"}</span>
                            <span className="flex gap-2">
                              <Button size="sm" variant="ghost" className="h-7" onClick={() => setConfirm({ kind: "restore", row: r })} data-testid={`mobile-numbers-restore-${r.mobile}`}><RotateCcw className="h-3 w-3 mr-1" /> Restore</Button>
                              <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => setConfirm({ kind: "purge", row: r })} data-testid={`mobile-numbers-purge-recycled-${r.mobile}`}><Trash2 className="h-3 w-3 mr-1" /> Delete permanently</Button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {data.confirmations?.length > 0 && (
                  <Card className="shadow-sm">
                    <CardContent className="p-4">
                      <p className="text-sm font-medium flex items-center gap-1.5 mb-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" /> Verification log</p>
                      <ul className="space-y-1 border-l-2 border-border pl-3" data-testid="mobile-numbers-confirmations">
                        {data.confirmations.slice(0, 20).map((c) => (
                          <li key={c.id} className="text-xs text-muted-foreground"><span className="tabular-nums text-foreground">{fmtMobile(c.mobile)}</span> confirmed correct by {c.by_label} · {fmtDateTime(c.at)}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>
            )
          )}

          {tab === "bin" && (
            <Card className="shadow-sm" data-testid="mobile-numbers-bin">
              <CardContent className="p-0">
                {!bin ? <Spinner /> : bin.items.length === 0 ? (
                  <EmptyState title="Recycle bin is empty" subtitle="Old numbers you recycle from any property in this municipality appear here until restored or permanently deleted." />
                ) : (
                  <>
                    <Table data-testid="mobile-numbers-bin-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>PIN / owner</TableHead>
                          <TableHead>Old number</TableHead>
                          <TableHead>Entered by</TableHead>
                          <TableHead>Recycled</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bin.items.map((r) => (
                          <TableRow key={r.id} data-testid={`mobile-numbers-bin-row-${r.mobile}`}>
                            <TableCell>
                              <button type="button" className="font-mono text-sm text-primary hover:underline" onClick={() => { setPin(r.pin); setTab("history"); setTimeout(() => lookup(), 0); }} data-testid={`mobile-numbers-bin-pin-${r.pin}`}>{r.pin}</button>
                              <p className="text-xs text-muted-foreground">{r.owner_name || "—"} · current {r.current_mobile ? fmtMobile(r.current_mobile) : "—"}</p>
                            </TableCell>
                            <TableCell className="font-medium tabular-nums">{fmtMobile(r.mobile)}</TableCell>
                            <TableCell><ActorCell row={r} /></TableCell>
                            <TableCell className="text-xs">{fmtDateTime(r.recycled_at)}<p className="text-[11px] text-muted-foreground">by {r.recycled_by_name || "Super Admin"}</p></TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button size="sm" variant="outline" className="h-8" onClick={() => setConfirm({ kind: "restore", row: r })} data-testid={`mobile-numbers-bin-restore-${r.mobile}`}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Restore</Button>
                                <Button size="sm" variant="outline" className="h-8 text-destructive border-destructive/40 hover:bg-destructive/10" onClick={() => setConfirm({ kind: "purge", row: r })} data-testid={`mobile-numbers-bin-purge-${r.mobile}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete permanently</Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="px-4 pb-3 border-t">
                      <PaginationBar page={bin.page} limit={bin.limit} total={bin.total} loading={binLoading} onPageChange={setBinPage} testId="mobile-numbers-bin-pagination" />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(v) => { if (!v && !busy) { setConfirm(null); setTyped(""); } }}>
        <AlertDialogContent data-testid="mobile-numbers-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {confirm?.kind === "purge" ? <AlertTriangle className="h-5 w-5 text-destructive" /> : confirm?.kind === "restore" ? <RotateCcw className="h-5 w-5 text-primary" /> : <Recycle className="h-5 w-5 text-primary" />}
              {confirm?.kind === "recycle" ? "Move this number to the recycle bin?" : confirm?.kind === "restore" ? "Restore this number to the history?" : "Delete this number permanently?"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p><span className="font-semibold tabular-nums text-foreground">{confirm ? fmtMobile(confirm.row.mobile) : ""}</span> — entered by {confirm?.row.by_label} on {confirm ? fmtDateTime(confirm.row.at) : ""} (PIN {confirm?.row.pin}).</p>
                {confirm?.kind === "recycle" && <p>It disappears from every history view (admin, supervisor, collector) but can be restored from the Recycle Bin.</p>}
                {confirm?.kind === "restore" && <p>It becomes visible again in the property's Number History.</p>}
                {confirm?.kind === "purge" && (
                  <>
                    <p className="text-destructive font-medium">This cannot be undone. The record of this old number and who entered it will be gone.</p>
                    <div className="space-y-1">
                      <Label htmlFor="mobile-numbers-purge-typed" className="text-xs">Type <span className="font-mono font-semibold">DELETE</span> to confirm</Label>
                      <Input id="mobile-numbers-purge-typed" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" data-testid="mobile-numbers-purge-typed" />
                    </div>
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="mobile-numbers-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); act(); }} disabled={busy || !purgeOk}
              className={confirm?.kind === "purge" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
              data-testid="mobile-numbers-confirm-button">
              {busy ? "Working…" : confirm?.kind === "recycle" ? "Recycle" : confirm?.kind === "restore" ? "Restore" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
